import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { 
  FaCheckCircle, 
  FaUser,
  FaRobot,
  FaSearch,
  FaFilter,
  FaTag,
  FaClock,
  FaEye
} from 'react-icons/fa';
import { 
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { useTheme } from '../../theme/context/ThemeContext';
import { toast } from 'react-hot-toast';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

/**
 * Memory Section Component - Simple Memory Log Approach
 * 
 * Shows a clear, transparent log of what information is being remembered
 * Simple table format that anyone can understand immediately
 */
const MemorySection = () => {
  const { darkMode } = useTheme();
  const [memoryLog, setMemoryLog] = useState([]);
  const [stats, setStats] = useState({
    totalMemories: 0,
    thisWeek: 0,
    uniqueTags: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('all');

  // Toast styles for dark mode
  const toastStyle = useMemo(() => ({
    style: {
      background: darkMode ? '#333' : '#fff',
      color: darkMode ? '#fff' : '#000',
      border: `1px solid ${darkMode ? '#444' : '#e2e8f0'}`,
    },
    success: {
      style: {
        background: darkMode ? '#0c4a2d' : '#ecfdf5',
        color: darkMode ? '#fff' : '#065f46',
        border: `1px solid ${darkMode ? '#065f46' : '#d1fae5'}`,
      },
    },
    error: {
      style: {
        background: darkMode ? '#4c1d24' : '#fef2f2',
        color: darkMode ? '#fff' : '#b91c1c',
        border: `1px solid ${darkMode ? '#b91c1c' : '#fee2e2'}`,
      },
    },
  }), [darkMode]);

  const loadMemoryLog = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/memory-log`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: {
            limit: 50,
            search: searchTerm,
            filter: selectedFilter
          }
        }
      );
      
      if (response.data.success) {
        setMemoryLog(response.data.data.entries || []);
        setStats(response.data.data.stats || {
          totalMemories: 0,
          thisWeek: 0,
          uniqueTags: 0
        });
      } else {
        throw new Error(response.data.error || 'Failed to load memory log');
      }
      
    } catch (error) {
      console.error('Error loading memory log:', error);
      setError('Unable to load memory information. Please try again.');
      toast.error('Failed to load memory data', toastStyle.error);
    } finally {
      setLoading(false);
    }
  }, [toastStyle.error, searchTerm, selectedFilter]);

  useEffect(() => {
    loadMemoryLog();
  }, [loadMemoryLog]);

  // Reload data when search or filter changes (with debounce for search)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadMemoryLog();
    }, searchTerm ? 300 : 0); // Debounce search by 300ms
    
    return () => clearTimeout(timeoutId);
  }, [searchTerm, selectedFilter, loadMemoryLog]);

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    
    // Same day
    if (date.toDateString() === now.toDateString()) {
      return `Today, ${date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      })}`;
    }
    
    // This week
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 7) {
      return `${date.toLocaleDateString('en-US', { weekday: 'short' })}, ${date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      })}`;
    }
    
    // Older
    return `${date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric'
    })}, ${date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    })}`;
  };





  if (loading) {
    return (
      <div className="memory-section space-y-6 mt-2 rounded-2xl max-h-[calc(100vh-120px)] overflow-y-auto scrollbar-thin">
        <SkeletonTheme baseColor={darkMode ? "#202020" : "#e6e6e6"} highlightColor={darkMode ? "#333" : "#d8d8d8"}>
          {/* Header skeleton */}
          <div className="space-y-2">
            <Skeleton height={28} width={300} />
            <Skeleton height={16} width={450} />
          </div>
          
          {/* Overview cards skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="border border-gray-200 dark:border-[#444444] rounded-xl p-4 space-y-2 bg-[#f5f5f5] dark:bg-[#181818]">
                <Skeleton height={16} width={100} />
                <Skeleton height={24} width={60} />
              </div>
            ))}
          </div>
          
          {/* Table skeleton */}
          <div className="border border-gray-200 dark:border-[#444444] rounded-xl p-4 bg-[#f5f5f5] dark:bg-[#181818]">
            <Skeleton height={20} width={200} className="mb-4" />
            {[...Array(5)].map((_, i) => (
              <div key={i} className="flex items-center space-x-4 py-3 border-b border-gray-200 dark:border-[#444444] last:border-b-0">
                <Skeleton height={16} width={120} />
                <Skeleton height={16} width={80} />
                <Skeleton height={16} width={300} />
                <Skeleton height={16} width={150} />
              </div>
            ))}
          </div>
        </SkeletonTheme>
      </div>
    );
  }

  if (error) {
    return (
      <div className="memory-section space-y-6">
        <div className="text-center py-12">
          <FaCheckCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 dark:text-red-400">{error}</p>
          <button
            onClick={loadMemoryLog}
            className="mt-4 px-4 py-2 border border-black dark:border-white text-sm font-medium rounded-lg shadow-sm text-white bg-black hover:bg-gray-800 dark:bg-white dark:text-black dark:hover:bg-gray-200 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="memory-section">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Organizational Memory</h2>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          See exactly what information is being remembered and by whom
        </p>
      </div>

      {/* Scrollable Container */}
      <div className="max-h-[calc(100vh-200px)] overflow-y-auto scrollbar-thin space-y-6" style={{ scrollbarWidth: 'thin', scrollbarColor: darkMode ? 'rgba(255, 255, 255, 0.1) transparent' : 'rgba(0, 0, 0, 0.2) transparent' }}>
        
        {/* Overview Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="border border-gray-200 dark:border-[#444444] rounded-xl p-4 bg-[#f5f5f5] dark:bg-[#181818]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Total Memories</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.totalMemories}</p>
              </div>
              <FaEye className="w-6 h-6 text-blue-500" />
            </div>
          </div>
          
          <div className="border border-gray-200 dark:border-[#444444] rounded-xl p-4 bg-[#f5f5f5] dark:bg-[#181818]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">Auto-Tags Created</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.uniqueTags}</p>
              </div>
              <FaTag className="w-6 h-6 text-green-500" />
            </div>
          </div>
          
          <div className="border border-gray-200 dark:border-[#444444] rounded-xl p-4 bg-[#f5f5f5] dark:bg-[#181818]">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500 dark:text-gray-400">This Week</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.thisWeek}</p>
              </div>
              <FaClock className="w-6 h-6 text-orange-500" />
            </div>
          </div>
        </div>

        {/* Search and Filter */}
        <div className="border border-gray-200 dark:border-[#444444] rounded-xl p-4 bg-[#f5f5f5] dark:bg-[#181818]">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search memories, people, or tags..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-[#444444] rounded-lg bg-[#f5f5f5] dark:bg-[#181818] text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-black focus:border-black dark:focus:ring-white dark:focus:border-white"
                />
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <FaFilter className="text-gray-500 dark:text-gray-400 w-4 h-4" />
              <select
                value={selectedFilter}
                onChange={(e) => setSelectedFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-[#444444] rounded-lg bg-[#f5f5f5] dark:bg-[#181818] text-gray-900 dark:text-white focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent dark:focus:border-white"
              >
                <option value="all">All Sources</option>
                <option value="user">Added by People</option>
                <option value="system">Added by System</option>
              </select>
            </div>
          </div>
        </div>

        {/* Memory Log Table */}
        <div className="border border-gray-200 dark:border-[#444444] rounded-xl overflow-hidden bg-[#f5f5f5] dark:bg-[#181818]">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-[#444444] bg-gray-50 dark:bg-[#333333]">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Memory Log</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Complete transparency into what information is being stored
            </p>
          </div>
          
          {memoryLog.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 dark:bg-[#333333] border-b border-gray-200 dark:border-[#444444]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Date & Time
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Added By
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Memory Added
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                      Auto-Tags
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-[#f5f5f5] dark:bg-[#181818] divide-y divide-gray-200 dark:divide-[#444444]">
                  {memoryLog.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-[#111111] transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {formatDateTime(item.dateTime)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {item.addedByType === 'user' ? (
                            <FaUser className="w-4 h-4 text-blue-500 mr-2" />
                          ) : (
                            <FaRobot className="w-4 h-4 text-green-500 mr-2" />
                          )}
                          <span className="text-sm text-gray-900 dark:text-white">{item.addedBy}</span>
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {item.source}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white max-w-md">
                        <div className="line-clamp-2">
                          {item.memoryAdded}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap gap-1">
                          {item.autoTags.map((tag, index) => (
                            <span
                              key={index}
                              className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-800 dark:text-blue-100 border border-blue-200 dark:border-blue-700"
                            >
                              {tag}
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-6 py-12 text-center">
              <FaSearch className="w-8 h-8 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400 mb-2">No memories found</p>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Try adjusting your search or filter criteria
              </p>
            </div>
          )}
        </div>

        {/* Info Panel */}
        <div className="border border-gray-200 dark:border-[#444444] rounded-xl p-6 bg-[#f5f5f5] dark:bg-[#181818]">
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-start">
              <InformationCircleIcon className="w-5 h-5 text-blue-600 dark:text-blue-400 mr-3 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <h4 className="font-medium mb-2">How Memory Works</h4>
                <ul className="space-y-1 text-xs">
                  <li>• <strong>User Additions:</strong> When team members share important information</li>
                  <li>• <strong>System Additions:</strong> When Midus automatically identifies key insights</li>
                  <li>• <strong>Auto-Tags:</strong> AI automatically categorizes each memory for easy searching</li>
                  <li>• <strong>Complete Transparency:</strong> You can see exactly what's stored and who added it</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemorySection;